import React, { useState, useEffect } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import Banner from './Banner';
import PollClosedScreen from './PollClosedScreen';
import useUnifiedAnalytics from '../analytics/useUnifiedAnalytics';
import { EVENTS } from '../analytics/events';

const VotingScreen = ({ onSelectContestant, pollConfig, siteConfig }) => {
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [isPollEnded, setIsPollEnded] = useState(false);
  const { client } = useStatsigClient();
  const analytics = useUnifiedAnalytics();
  
  // Track voting page view on component mount
  useEffect(() => {
    analytics.trackEvent(EVENTS.PAGE_VIEW, 'voting');
  }, [analytics]);
  
  // Calculate and update countdown timer based on poll end_date
  useEffect(() => {
    // Get end_date from poll configuration or use a default
    const endTimeStr = pollConfig?.end_date;
    
    if (!endTimeStr) {
      setCountdown('--d --h --m --s');
      return;
    }
    
    // Parse the end time
    const endTime = new Date(endTimeStr);
    
    const updateCountdown = () => {
      const now = new Date();
      const timeDiff = endTime - now;
      
      if (timeDiff <= 0) {
        // Poll has ended
        setCountdown('0d 00h 00m 00s');
        setIsPollEnded(true);
        return;
      }
      
      // Calculate days, hours, minutes, seconds
      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
      
      // Format the countdown string
      setCountdown(
        `${days}d ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`
      );
    };
    
    // Check if poll has already ended
    const now = new Date();
    if (endTime <= now) {
      setIsPollEnded(true);
      setCountdown('0d 00h 00m 00s');
    }
    
    // Update immediately
    updateCountdown();
    
    // Set interval to update every second
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, [pollConfig]);
  
  const handleSelectContestant = (contestant) => {
    setSelectedContestant(contestant);
    
    // Track character selection with unified analytics service
    // This will track in both Statsig and Google Analytics
    analytics.trackEvent(EVENTS.CHARACTER_SELECTED, contestant.id, {
      character_name: contestant.display_name,
      selection_time: new Date().toISOString()
    });
  };
  
  const handleSubmit = () => {
    if (selectedContestant) {
      // Log successful submit button click
      client?.logEvent("submit_button_clicked", "active", {
        character_id: selectedContestant.id,
        character_name: selectedContestant.display_name,
        click_time: new Date().toISOString()
      });
      
      onSelectContestant(selectedContestant);
    } else {
      // Log inactive submit button click
      client?.logEvent("submit_button_clicked", "inactive", {
        click_time: new Date().toISOString(),
        reason: "no_character_selected"
      });
    }
  };
  
  // Get contestants from pollConfig
  const contestants = pollConfig?.allowed_contestants || [];
  
  // If poll has ended, show the PollClosedScreen
  if (isPollEnded) {
    return <PollClosedScreen pollConfig={pollConfig} />;
  }

  return (
    <div className="min-h-screen bg-toon-yellow p-4">
      <Banner text={siteConfig?.banner} />
      {/* Header */}
      <div className="text-center mb-10">
        <img 
          src="/assets/Toon_House_Logo.svg" 
          alt="Toon House Logo" 
          className="h-24 mx-auto mb-4"
        />
        <h1 className="text-3xl font-bold mb-2">{pollConfig?.title || 'TOON HOUSE VOTING!'}</h1>
        <p className="text-lg mb-2">{pollConfig?.instructions || 'Vote for your favorite character!'}</p>
        <p className="text-xl font-semibold text-red-500">{countdown} LEFT TO VOTE</p>
      </div>
      
      {/* Contestants Section */}
      <div className="mb-8 pt-4">
        {/* Mobile: Horizontally scrollable */}
        <div className="flex overflow-x-auto pb-4 pt-2 px-2 md:hidden">
          {contestants.map((contestant) => (
            <div 
              key={contestant.id}
              className={`flex-shrink-0 w-72 mx-2 my-2 rounded-2xl shadow-lg overflow-hidden max-w-md ${
                selectedContestant?.id === contestant.id 
                  ? 'ring-4 ring-green-500 ring-offset-2 ring-offset-toon-yellow' 
                  : ''
              }`}
              onClick={() => handleSelectContestant(contestant)}
            >
              {/* Card with image taking most of the space */}
              <div className="relative">
                {/* Character Image */}
                <div className="w-fit mx-auto overflow-hidden flex justify-center items-center bg-white" style={{ minHeight: '200px' }}>
                  <img 
                    src={contestant.image_url} 
                    alt={contestant.display_name} 
                    className="h-auto object-contain" 
                    style={{ maxHeight: '300px', width: 'auto' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300?text=' + contestant.display_name;
                    }}
                  />
                </div>
                
                {/* Selection indicator */}
                {selectedContestant?.id === contestant.id && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Text area at bottom */}
              <div className="bg-gray-900 text-white p-4 w-full">
                <h3 className="text-xl font-bold uppercase">{contestant.display_name}</h3>
                <p className="text-sm text-gray-300">{contestant.description || ''}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Desktop: Grid format */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {contestants.map((contestant) => (
            <div 
              key={contestant.id}
              className={`rounded-2xl shadow-lg overflow-hidden cursor-pointer m-2 mx-auto w-fit ${
                selectedContestant?.id === contestant.id 
                  ? 'ring-4 ring-green-500 ring-offset-2 ring-offset-toon-yellow' 
                  : ''
              }`}
              onClick={() => handleSelectContestant(contestant)}
            >
              {/* Card with image taking most of the space */}
              <div className="relative">
                {/* Character Image */}
                <div className="w-fit mx-auto overflow-hidden flex justify-center items-center bg-white" style={{ minHeight: '200px' }}>
                  <img 
                    src={contestant.image_url} 
                    alt={contestant.display_name} 
                    className="h-auto object-contain" 
                    style={{ maxHeight: '300px', width: 'auto' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300?text=' + contestant.display_name;
                    }}
                  />
                </div>
                
                {/* Selection indicator */}
                {selectedContestant?.id === contestant.id && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Text area at bottom - width matches the image container */}
              <div className="bg-gray-900 text-white p-4 w-full">
                <h3 className="text-xl font-bold uppercase">{contestant.display_name}</h3>
                <p className="text-sm text-gray-300">{contestant.description || ''}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          className={`py-3 px-8 rounded-full font-bold text-white ${
            selectedContestant 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-gray-300 cursor-not-allowed'
          }`}
          disabled={!selectedContestant}
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default VotingScreen;
